import React, { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import tempJson from "../../../json/fallbackglobalvariables.json";
import Globals, { URLS } from "../../../utility/constants/appConstants";
import { useDispatch, useSelector } from "react-redux";
import getPrefrencesClient from "../../../utility/apiClients/customer/getPrefrencesClient";
import ViewPaymentMethodsClient from "../../../utility/apiClients/customer/ViewPaymentMethodsClient";
import styles from "../../../styles/myAccount/accountSettings.module.scss";
import { easyPayCalculate } from "../../../utility/features/helpers/accountHelper";
import accountDetails from "../../../utility/apiClients/account/accountDetailsClient";
import { setCustomerDetails } from "../../../reduxStore/myAccount/getCustomerDetails";
import Loader from "../../../components/loader";
import webStorage from "../../../utility/utils/webStorage";
import ErrorBlock from '../../errorMessage';
import store from "../../../reduxStore/store";
import customerSummaryClient from "../../../utility/apiClients/customer/customerSummaryClient";
import { setCustomerInfo } from "../../../reduxStore/login/loginSlice";
import missionStatusClient from "../../../utility/apiClients/user/missionStatusClient";

function AccountSettingsTile({ title, desc, cta, ctaLink, children }) {
  return (
    <div className={`${styles["card"]}`}>
      <div className={`${styles["cardInner"]}`}>
        <div className={`${styles["cardHeader"]}`}>
          <div className={styles["gfs-26"]}>{title}</div>
          {desc &&
            desc.replace(/(<([^>]+)>)/gi, "").toLowerCase() !==
            title.replace(/(<([^>]+)>)/gi, "").toLowerCase() && (
              <div
                className={styles["gfs-16"]}
                dangerouslySetInnerHTML={{ __html: desc }}
              />
            )}
        </div>

        <div className="card-body">{children}</div>

        {cta && ctaLink && (
          <div className={`${styles["cardFooter"]}`}>
            <Link
              to={ctaLink}
              className={`${styles["btn"]} ${styles["ctaBtn"]}`}
            >
              {cta}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const AccountSettings = ({ customerIB }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const nav = useNavigate();
  const customerInfo = useSelector((state) => state.login.customerInfo);
  const [customerSummary, setCustomerSummary] = useState(
    store.getState().login.customerInfo,
  );

  const selectedServiceAddress = useSelector(
    (state) => state.accountSummary.selectedServiceAddress
  );

  const custNumbers = JSON.parse(localStorage.getItem("customerNumbers")) || [];

  const [serviceAddress, customerNumber] = useMemo(() => {
      let custNumber = "";
      let serviceAddr = "";
      if(selectedServiceAddress != null){
        [serviceAddr, custNumber] = selectedServiceAddress.split(" | ");
      } else {
        custNumber = customerInfo?customerInfo?.[0].CustomerNumber:custNumbers[0];
        serviceAddr = customerInfo?customerInfo?.[0].ServiceAddresses[0].address:"";
      }
      
    return [serviceAddr, custNumber];
  }, [selectedServiceAddress, customerInfo]);

  const [error, setErrorMsg] = useState("");
  const allCustomersApiCalled = useSelector((state) => state.login.allCustomersApiCalled);
  const [svcAgData, setSvcAgData] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [easyPayCount, setEasyPayCount] = useState(0);
  const [emailAddress, setEmailAddress] = useState(null);
  const [billingAddress, setBillingAddress] = useState(null);
  const [customerNumbers, setCustomerNumbers] = useState(null);
  const phoneNumberForEcom  = customerSummary ? customerSummary[0]?.Phone : "";
  const emailAddressForEcom = customerSummary ? customerSummary[0]?.EmailAddress : "";
  const {paymentMethodUpdate} = location.state || false;
  const [currentController, setCurrentController] = useState(null);

  const missionStatusCheck = async () => {
    try {
      const missionStatus = await missionStatusClient();
      return !["yes"].includes(String(missionStatus?.data).trim().toLowerCase());
    } catch (err) {
      console.error("Error checking mission status api:", err);
      return false;
    }
  };

  const getCustomerSummary = async (custNum) => {
    if (custNum === "") return;

    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      return;
    }

    try {
      const customerSummary = await customerSummaryClient(custNum);

      if (customerSummary?.status && customerSummary?.status === 401) {
        console.log("Unauthorized access - 401");
        localStorage.clear();
        nav("/login", { replace: true });
        return;
      }

      if (
        customerSummary?.data === null ||
        customerSummary?.data === undefined
      ) {
        setErrorMsg("Some error occurred.");
        return;
      }

      dispatch(setCustomerInfo(customerSummary?.data));
      setCustomerSummary([customerSummary?.data]);
    } catch (err) {
      setErrorMsg("Some error occurred.");
    } 
  };

  useEffect(() => {
    if (customerSummary === null) {
      (async () => {
        await getCustomerSummary(customerNumber);
      })();
    }
  }, [customerSummary]);

  const fetchPayments = async (cusNum, signal=null) => {
    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      return;
    }

    const paymentInfo = await ViewPaymentMethodsClient({ cusNum }, signal);

    if(paymentInfo?.status === 401 || paymentInfo?.errorStatus === 401){
      console.log("Unauthorized access - 401");
      localStorage.clear();
      nav("/login", { replace: true });
      return;
    }

    if(paymentInfo?.status === 500) {
      setErrorMsg("Some error occurred.");
      return;
    } 

    if(!paymentInfo.error){
      setPaymentDetails(paymentInfo?.data?.PaymentMethods || []);
    } else {
      if(paymentInfo?.error !== "signal is aborted without reason")
      setErrorMsg("Some error occurred.");
    }
  };

  const fetchAccountDetails = async (cusNum, serviceAddress, signal) => {
    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      return;
    }

    setSvcAgData(null);
    const response = await accountDetails(cusNum, serviceAddress, signal);
    if (response?.status === 401) {
      console.log("Unauthorized access - 401");
      localStorage.clear();
      nav("/login", { replace: true });
      return;
    }

    if (response?.status === 424) {
      setErrorMsg("Some error occurred.");
      return;
    }

    if(response?.status === 500) {
      setErrorMsg("Some error occurred.");
      return;
    } 

    if (response?.SalesAgreements && !response.errorMsg) {
      dispatch(setCustomerDetails(response));
      const { easyPaySvcAg } = easyPayCalculate(response.SalesAgreements);
      setSvcAgData(easyPaySvcAg);
      setEasyPayCount(easyPaySvcAg.length);
    } else {
      if(response?.errorMsg !== "signal is aborted without reason")
      setErrorMsg("Some Error Occurred.");
    }
  };

const isAccountSettingsOverview = location.pathname === "/my-account/account-settings";

useEffect(() => {
  if(paymentMethodUpdate == true && isAccountSettingsOverview) {
    setPaymentDetails(null);
    fetchPayments(customerNumber);
    nav(location.pathname, { replace: true, state: { paymentMethodUpdate: false } });
  }
}, [paymentMethodUpdate, isAccountSettingsOverview]);

  useEffect(() => {
    // paymentDetails/svcAgData are only rendered on the overview page, so skip
    // these fetches (getpaymentmethods/accountDetails) on nested routes like payment-methods
    if (!customerNumber || !isAccountSettingsOverview) return;

    if (currentController) {
        currentController.abort();
    }

    setPaymentDetails(null);
    
    let controller = new AbortController();
    setCurrentController(controller);
    const signal = controller.signal;

    fetchPayments(customerNumber, signal);
    if(allCustomersApiCalled)
      fetchAccountDetails(customerNumber, serviceAddress, signal);

    setEmailAddress(customerInfo?.[0]?.EmailAddress || null);
    const billingAddresses = customerInfo?.[0]?.BillingAddresses || [];
    if (billingAddresses.length > 0) {
      setBillingAddress(billingAddresses[billingAddresses.length - 1]);

    }

    return () => {
      setErrorMsg("");
    }
  }, [customerNumber, customerInfo, selectedServiceAddress, allCustomersApiCalled, isAccountSettingsOverview]);

  useEffect(() => {
    const storedCustomerNumbers = webStorage.local.get("customerNumbers");
    if (Array.isArray(storedCustomerNumbers)) {
      setCustomerNumbers(storedCustomerNumbers);
    }
  }, [selectedServiceAddress]);

  const labels = tempJson.acc;

  return location.pathname === "/my-account/account-settings" ? (
    <>
      {error !== "" && <ErrorBlock labels={labels} errMsg={error} />}
      <div className={styles?.pageWrapper}>
        {error === "" && svcAgData ? (
          <>
            <div className={`${styles["centerBox"]}`}>
              <div className={`${styles["grid"]}`}>
                {/* Billing */}
                {customerIB !== "ACTIVE" && (
                  <AccountSettingsTile
                    title={labels.billingTitle}
                    desc={""}
                    cta={labels.billingCta}
                  >
                    {billingAddress && (
                      <div className={styles["gfs-16"]}>
                        <div className={styles["mb-30"]}>
                          <div className={styles["gfw-3"]}>
                            {labels.billingCrrentBillAdd}
                          </div>
                          <div
                            className={`${styles["gfs-14"]} { ${styles["gfw-7"]} ${styles["text-green-dark"]}`}
                          >
                            {billingAddress.address}, {billingAddress.city},{" "}
                            {billingAddress.state} {billingAddress.postalCode}
                          </div>
                        </div>
                      </div>
                    )}
                  </AccountSettingsTile>
                )}

                {/* Email and Communication */}
                <AccountSettingsTile
                  title={labels.emailTitle}
                  cta={labels.emailCta}
                >
                  <div className={`${styles["gfs-16"]} ${styles["pl-30"]}`}>
                    <div className={styles["mb-30"]}>
                      <div className={styles["gfw-3"]}>
                        {labels.currentEmailAddText}
                      </div>
                      {emailAddressForEcom && (
                        <div
                          className={`${styles["gfs-14"]} ${styles["gfw-7"]} ${styles["text-green-dark"]}`}
                        >
                          {emailAddressForEcom}
                        </div>
                      )}
                      {emailAddressForEcom === "" && (
                        <div
                          className={`${styles["gfs-14"]} ${styles["gfw-7"]} ${styles["text-green-dark"]}`}
                        >
                          {labels.noEmail}
                        </div>
                      )}
                    </div>
                    <div className={styles["mb-30"]}>
                      <div className={styles["gfw-3"]}>
                        {labels.currentPhoneNoText}
                      </div>
                      {phoneNumberForEcom && (
                        <div
                          className={`${styles["gfs-14"]} ${styles["gfw-7"]} ${styles["text-green-dark"]}`}
                        >
                          {phoneNumberForEcom}
                        </div>
                      )}

                      {phoneNumberForEcom === "" && (
                        <div
                          className={`${styles["gfs-14"]} ${styles["gfw-7"]} ${styles["text-green-dark"]}`}
                        >
                          {labels.noPhoneNoText}
                        </div>
                      )}
                    </div>
                  </div>
                </AccountSettingsTile>

                {/* Payment Methods */}
                <AccountSettingsTile
                  title={labels.paymentTitle}
                  desc={labels.paymentDesc}
                  cta={labels.managePaymentOptions}
                  ctaLink={URLS.paymentMethods}
                >
                  <div className={`${styles["gfs-16"]} ${styles["pl-30"]}`}>
                    {paymentDetails === null && <div>
                      Loading payment methods...
                      <img height="35" width="35" src={"/images/loading-animation.gif"} alt="loading_icon" />
                    </div>}
                    {paymentDetails && paymentDetails.length > 0
                     ? <>
                        <div>{labels.paymentFileText}</div>
                    
                        {paymentDetails.slice(0, 2).map((p, idx) => (
                          <div
                            key={idx}
                            className={`${styles["gfs-14"]} ${styles["gfw-7"]} ${styles["text-green-dark"]}`}
                          >
                            {p.pccPaymentType === "BankAccount"
                              ? `${p.pccPaymentType} ending in ${p.pccAcctLast4}`
                              : `${p.pccPaymentType} ending in ${p.pccAcctLast4}`}
                          </div>
                        ))}
                        {paymentDetails.length > 2 && (
                          <div
                            className={`${styles["gfs-14"]} ${styles["gfw-7"]} ${styles["text-green-dark"]}`}
                          >
                            ...
                          </div>
                        )}
                      </>
                      
                     : (
                        paymentDetails && paymentDetails.length === 0 && <div>{labels.paymentNoFileText}</div>
                      )
                    }
                  </div>
                </AccountSettingsTile>

                {/* Password */}
                <AccountSettingsTile
                  title={labels.passwordTitle}
                  desc={labels.passwordDesc}
                  cta={labels.passwordCta}
                  ctaLink={URLS.changePassword}
                />

                {/* customer numbers  */}
                <AccountSettingsTile
                  title={labels.serviceTitle}
                  desc={labels.serviceDesc}
                  cta={labels.custLinkText}
                >
                  {customerNumbers ? (
                    <div className={`${styles["gfs-16"]} ${styles["mb-20"]}`}>
                      <div className={styles["mb-20"]}>
                        <div>
                          <span>{customerNumbers.length}</span>
                          {labels.custLinkText ?? ""}
                        </div>
                      </div>

                      {customerNumbers.map((number) => (
                        <div className={styles["gfs-16"]} key={String(number)}>
                          {number}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </AccountSettingsTile>

                {/* EasyPay */}
                <AccountSettingsTile
                  title={labels.easyPayTitle}
                  desc={labels.easyPayDesc}
                  cta={labels.easyPayCta}
                  ctaLink={URLS.easyPaySettings}
                >
                  <div className="gfs-16 px-md-30">
                    <div className="mb-30">
                      <div className="gfw-3">
                        {labels.easyPayText1}
                        <span className="gfw-7 text-green-dark">
                          {easyPayCount}
                        </span>
                        {labels.easyPayText2}
                        <span className="font-italic">
                          <strong className="gfw-7">{labels.easyText}</strong>
                          {labels.pay}
                        </span>
                      </div>
                    </div>
                  </div>
                </AccountSettingsTile>
              </div>
            </div>
          </>
        ) : (
          error == "" && <Loader></Loader>
        )}
      </div>
    </>
  ) : (
    <Outlet />
  );
};

export default AccountSettings;
